import React, { useCallback } from 'react';
import classNames from 'classnames';
import Icon from 'components/styleguide/icon/Icon';

const styles = require('./OptionButton.module.scss');

type Context = 'talent' | 'company' | 'neutral';
type Mode = 'single' | 'multi';

export interface OptionButtonProps {
  context?: Context;
  label: string;
  mode?: Mode;
  onChange?: (selected: boolean) => void;
  selected?: boolean;
}

const OptionButton: React.FC<OptionButtonProps> = ({
  context = 'talent',
  label,
  mode = 'multi',
  onChange,
  selected = false,
}) => {
  const isSingle = mode === 'single';

  const handleClick = useCallback(() => {
    if (isSingle && selected) return;
    onChange?.(!selected);
  }, [isSingle, onChange, selected]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  }, [handleClick]);

  return (
    <div
      className={classNames(styles.chip, styles[context], styles[mode], {
        [styles.selected]: selected,
      })}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role={isSingle ? 'radio' : 'button'}
      tabIndex={0}
      aria-checked={isSingle ? selected : undefined}
      aria-pressed={isSingle ? undefined : selected}
      aria-label={label}
    >
      {isSingle && (
        <span className={styles.radioIcon}>
          <Icon
            name={selected ? 'radioButtonChecked' : 'radioButtonUnchecked'}
            size="md"
            aria-hidden="true"
          />
        </span>
      )}
      {!isSingle && selected && (
        <Icon name="check" size="xs" color={context} aria-hidden="true" />
      )}
      <span className={styles.label}>{label}</span>
    </div>
  );
};

export default OptionButton;
